import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";

interface ContentRow {
  content_key: string;
  value_de: string;
  value_en: string;
  value_fr: string;
  value_es: string;
  value_it: string;
}

const langMap = {
  de: "value_de",
  en: "value_en",
  fr: "value_fr",
  es: "value_es",
  it: "value_it",
} as const;

let cachedData: ContentRow[] | null = null;
let fetchPromise: Promise<ContentRow[]> | null = null;

const fetchAll = async (): Promise<ContentRow[]> => {
  if (cachedData) return cachedData;
  if (fetchPromise) return fetchPromise;
  fetchPromise = (async () => {
    const { data } = await supabase
      .from("site_content")
      .select("content_key, value_de, value_en, value_fr, value_es, value_it");
    cachedData = data || [];
    fetchPromise = null;
    return cachedData;
  })();
  return fetchPromise;
};

export const useCmsContent = () => {
  const { language } = useLanguage();
  const [contents, setContents] = useState<ContentRow[]>(cachedData || []);

  useEffect(() => {
    fetchAll().then(setContents);
  }, []);

  const get = useCallback(
    (key: string): string | null => {
      const row = contents.find((c) => c.content_key === key);
      if (!row) return null;
      const val = row[langMap[language]];
      // Fall back to German if the current language value is empty
      return val || row.value_de || null;
    },
    [contents, language]
  );

  return { get, contents, loading: contents.length === 0 && !cachedData };
};

// Invalidate cache (call after admin saves)
export const invalidateCmsCache = () => {
  cachedData = null;
  fetchPromise = null;
};
